import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/components/ui/button';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { ConfirmDialog } from '@/shared/components/custom/confirm-dialog/ConfirmDialog';
import { RichTextEditor } from '@/shared/components/custom/rich-text-editor';
import { useConfirm } from '@/shared/hooks/useConfirm';
import { useNote } from '../hooks/useNote';
import { useNoteVersion } from '../hooks/useNoteVersion';
import { useDeleteNote, useUpdateNote } from '../hooks/useNoteMutations';
import { useRestoreNoteVersion } from '../hooks/useNoteVersionMutations';
import { notesService } from '../api/notes.service';
import { htmlToEditorJson, editorJsonToHtml } from '../adapters/content-html.adapter';
import { noteFormSchema } from '../schemas';
import { NoteToolbar } from '../components/NoteToolbar';
import { NoteTitleInput } from '../components/NoteTitleInput';
import { NoteVersionView } from '../components/NoteVersionView';
import { NoteHistoryDialog } from '../components/NoteHistoryDialog';
import type { NoteExportFormat } from '../types';

const AUTOSAVE_DELAY_MS = 2000;

export function NoteEditorPage() {
  const { t } = useTranslation('notes');
  const navigate = useNavigate();
  const { orgId, noteId } = useParams<{ orgId: string; noteId: string }>();

  const { data: note, isLoading, isError, refetch } = useNote(noteId);
  const updateNote = useUpdateNote(noteId ?? '');
  const deleteNote = useDeleteNote();
  const restoreVersion = useRestoreNoteVersion(noteId ?? '');
  const {
    confirm,
    isOpen: confirmOpen,
    options: confirmOptions,
    handleConfirm,
    handleCancel,
  } = useConfirm();

  const [title, setTitle] = useState('');
  const [contentJson, setContentJson] = useState<string>();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [viewingVersionId, setViewingVersionId] = useState<string | null>(null);

  const { data: viewingVersion } = useNoteVersion(noteId, viewingVersionId ?? undefined);

  // Re-syncs whenever the server content actually changes (initial load, our own
  // autosave echoed back post-sanitization, or a restore). Adjusted during render
  // (React's documented pattern for resetting state from props) instead of an
  // effect, so the sync is never one render late. `RichTextEditor` only applies
  // the new value if it actually differs from what's already in the editor, so
  // this doesn't disrupt active typing in the common case.
  const syncKey = note ? `${note.id}:${note.updatedAt}` : null;
  const [lastSyncedKey, setLastSyncedKey] = useState<string | null>(null);
  if (note && syncKey !== lastSyncedKey) {
    setLastSyncedKey(syncKey);
    setTitle(note.title ?? '');
    setContentJson(htmlToEditorJson(note.content ?? ''));
  }

  // Trailing debounce: autosave fires once typing pauses, not on every keystroke —
  // the shared `useDebouncedCallback` is leading-edge and would create a version
  // per burst instead of one per pause. Kept local, this feature is the only caller.
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleSave = (nextTitle: string, nextContentJson: string) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      const html = editorJsonToHtml(nextContentJson);
      const parsed = noteFormSchema.safeParse({ title: nextTitle || null, content: html });
      if (parsed.success) updateNote.mutate(parsed.data);
    }, AUTOSAVE_DELAY_MS);
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
    if (contentJson !== undefined) scheduleSave(value, contentJson);
  };

  const handleContentChange = (json: string) => {
    setContentJson(json);
    scheduleSave(title, json);
  };

  const handleDelete = async () => {
    const result = await confirm({
      title: t('delete.confirmTitle'),
      description: t('delete.confirmDescription'),
      variant: 'destructive',
    });
    if (!result.confirmed || !noteId) return;
    await deleteNote.mutateAsync(noteId);
    navigate(`/${orgId}/notes`);
  };

  const handleExport = (format: NoteExportFormat) => {
    if (noteId) void notesService.exportNote(noteId, format);
  };

  const handleRestoreFromBanner = async () => {
    if (!viewingVersionId) return;
    await restoreVersion.mutateAsync(viewingVersionId);
    setViewingVersionId(null);
  };

  if (isLoading) {
    return (
      <div
        className="flex h-full flex-col gap-4 p-6"
        aria-hidden="true"
        data-testid="notes-editor-loading-skeleton"
      >
        <Skeleton className="h-8 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  }

  if (isError || !note) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-3 p-8 text-center">
        <p className="text-sm text-destructive" data-testid="notes-editor-error-message">
          {t('editor.loadingError')}
        </p>
        <Button variant="outline" onClick={() => refetch()}>
          {t('editor.retry')}
        </Button>
      </div>
    );
  }

  if (viewingVersionId && viewingVersion) {
    return (
      <NoteVersionView
        version={viewingVersion}
        onClose={() => setViewingVersionId(null)}
        onRestore={handleRestoreFromBanner}
        isRestoring={restoreVersion.isPending}
      />
    );
  }

  return (
    <div className="flex h-full flex-col">
      <NoteToolbar
        note={note}
        isSaving={updateNote.isPending}
        onOpenHistory={() => setHistoryOpen(true)}
        onDelete={handleDelete}
        onExport={handleExport}
      />

      <div className="flex-1 overflow-y-auto px-6 py-4 md:px-10">
        <div className="max-w-3xl">
          <NoteTitleInput
            value={title}
            onChange={handleTitleChange}
            placeholder={t('editor.titlePlaceholder')}
          />
          <RichTextEditor
            value={contentJson}
            onChange={handleContentChange}
            enableFixedToolbar
            enableBubbleMenu={false}
            enableFloatingMenu={false}
            enableDragHandle={false}
            enableImages={false}
            enableVideoEmbed={false}
            enableMentions={false}
            enableCallouts={false}
            enableExpandable={false}
          />
        </div>
      </div>

      <NoteHistoryDialog
        noteId={noteId ?? ''}
        noteTitle={note.title}
        noteUpdatedAt={note.updatedAt}
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        onRestored={() => refetch()}
        onViewVersion={(versionId) => setViewingVersionId(versionId)}
      />

      {confirmOpen && confirmOptions && (
        <ConfirmDialog
          open={confirmOpen}
          onOpenChange={(open) => !open && handleCancel()}
          title={confirmOptions.title}
          description={confirmOptions.description}
          variant={confirmOptions.variant}
          onConfirm={handleConfirm}
          data-testid="notes-delete-confirm-dialog"
        />
      )}
    </div>
  );
}
